import {CSSProperties, ReactNode, useCallback, useEffect, useMemo, useState} from 'react'
import {Navigate, useNavigate, useParams} from 'react-router-dom'
import {
  Activity, AlertTriangle, BarChart3, Clock3, Database, Download, Eye, FileSpreadsheet,
  Gauge, Laptop, Layers3, Monitor, MousePointerClick, Radio, RefreshCw, Search,
  RotateCcw, Server, ShieldCheck, Smartphone, Timer, Users, Wifi, Zap,
} from 'lucide-react'
import {
  Area, AreaChart, Bar, BarChart, CartesianGrid, Cell, Legend, Pie, PieChart,
  ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts'
import * as XLSX from 'xlsx'
import {PageTitle} from '../../../_metronic/layout/core'
import {isSuperAdmin} from '../../modules/auth'
import {AnalyticsFilters, AnalyticsSection, getAnalyticsFilters, getAnalyticsReport} from './analyticsApi'
import ContentAnalytics, {ContentAnalyticsLoader, ContentKind} from './ContentAnalytics'
import './AnalyticsPage.scss'

const today = () => new Date().toISOString().slice(0, 10)
const daysAgo = (days: number) => {
  const value = new Date()
  value.setDate(value.getDate() - (days - 1))
  return value.toISOString().slice(0, 10)
}

const sections: Array<{id: AnalyticsSection; label: string; icon: typeof Activity}> = [
  {id: 'overview', label: 'Visão geral', icon: BarChart3},
  {id: 'screens', label: 'Telas', icon: Monitor},
  {id: 'users', label: 'Usuários', icon: Users},
  {id: 'features', label: 'Interações', icon: MousePointerClick},
  {id: 'content', label: 'Dashboards/Relatórios', icon: Database},
  {id: 'performance', label: 'Performance', icon: Gauge},
  {id: 'realtime', label: 'Tempo real', icon: Radio},
]

const colors = ['#3157d5', '#0e9f8a', '#e9a23b', '#7a58c7', '#dd5b69', '#4d91c7']
const formatNumber = (value: unknown) => new Intl.NumberFormat('pt-BR', {maximumFractionDigits: 0}).format(Number(value || 0))
const formatPercent = (value: unknown) => `${Number(value || 0).toLocaleString('pt-BR', {maximumFractionDigits: 1})}%`
const formatMs = (value: unknown) => {
  const ms = Number(value || 0)
  if (ms >= 60000) return `${(ms / 60000).toLocaleString('pt-BR', {maximumFractionDigits: 1})} min`
  if (ms >= 1000) return `${(ms / 1000).toLocaleString('pt-BR', {maximumFractionDigits: 1})} s`
  return `${Math.round(ms).toLocaleString('pt-BR')} ms`
}
const formatDate = (value: unknown, includeTime = true) => {
  if (!value) return '—'
  const date = new Date(String(value))
  if (Number.isNaN(date.getTime())) return '—'
  return new Intl.DateTimeFormat('pt-BR', includeTime
    ? {day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit'}
    : {day: '2-digit', month: 'short', year: 'numeric'}).format(date)
}
const initials = (name?: string) => (name || 'Usuário').split(' ').slice(0, 2).map((part) => part[0]).join('').toUpperCase()
type MetricProps = {
  label: string
  value: string
  icon: typeof Activity
  hint?: string
  color?: string
  background?: string
}

const Metric = ({label, value, icon: Icon, hint, color = '#3157d5', background = '#edf2ff'}: MetricProps) => (
  <div className='ia-metric' style={{'--metric-color': color, '--metric-bg': background} as CSSProperties}>
    <div className='ia-metric__top'>
      <span className='ia-metric__icon'><Icon size={16} /></span>
      {hint ? <span className='ia-metric__hint'>{hint}</span> : null}
    </div>
    <div className='ia-metric__label'>{label}</div>
    <div className='ia-metric__value'>{value}</div>
  </div>
)

const Panel = ({title, subtitle, badge, children}: {title: string; subtitle?: string; badge?: string; children: ReactNode}) => (
  <section className='ia-panel'>
    <div className='ia-panel__header'>
      <div>
        <h2 className='ia-panel__title'>{title}</h2>
        {subtitle ? <div className='ia-panel__subtitle'>{subtitle}</div> : null}
      </div>
      {badge ? <span className='ia-panel__badge'>{badge}</span> : null}
    </div>
    {children}
  </section>
)

const Empty = ({title = 'Ainda não há dados suficientes', description = 'A coleta já está ativa. Os indicadores aparecerão aqui conforme as pessoas utilizarem o Insight Experience.'}) => (
  <div className='ia-empty'>
    <span className='ia-empty__icon'><Database size={22} /></span>
    <h3>{title}</h3>
    <p>{description}</p>
  </div>
)

const SearchBox = ({value, onChange, placeholder}: {value: string; onChange: (value: string) => void; placeholder: string}) => (
  <div className='ia-search'>
    <Search size={14} />
    <input value={value} onChange={(event) => onChange(event.target.value)} placeholder={placeholder} />
  </div>
)

const ChartTooltip = ({active, payload, label}: any) => {
  if (!active || !payload?.length) return null
  return (
    <div style={{padding: '9px 11px', border: '1px solid #e4e7ec', borderRadius: 9, background: '#fff', boxShadow: '0 8px 24px rgba(20,30,55,.12)', fontSize: 10}}>
      <div style={{marginBottom: 5, color: '#667085', fontWeight: 700}}>{label && !Number.isNaN(new Date(String(label)).getTime()) ? formatDate(label, false) : String(label || payload[0]?.payload?.name || payload[0]?.name || '')}</div>
      {payload.map((item: any) => <div key={item.dataKey || item.name} style={{color: item.color, marginTop: 3}}>{item.name}: <strong>{formatNumber(item.value)}</strong></div>)}
    </div>
  )
}

const Overview = ({data}: {data: any}) => {
  const metrics = data?.metrics || {}
  if (!metrics.page_views && !data?.evolution?.length) return <Empty />
  return (
    <>
      <div className='ia-metrics'>
        <Metric label='Usuários ativos' value={formatNumber(metrics.online_users)} icon={Wifi} hint='30 min' color='#0e9f8a' background='#e9faf6' />
        <Metric label='Sessões recentes' value={formatNumber(metrics.active_sessions)} icon={Activity} hint='30 min' color='#1684c7' background='#eaf6fd' />
        <Metric label='Visualizações' value={formatNumber(metrics.page_views)} icon={Eye} />
        <Metric label='Sessões no período' value={formatNumber(metrics.sessions)} icon={Layers3} />
        <Metric label='Usuários com navegação' value={formatNumber(metrics.unique_users)} icon={Users} hint='no período' />
        <Metric label='Tempo médio por tela' value={formatMs(metrics.avg_screen_time_ms)} icon={Clock3} color='#7a58c7' background='#f3effd' />
        <Metric label='Latência média no servidor' value={formatMs(metrics.avg_api_latency_ms)} icon={Server} color='#1684c7' background='#eaf6fd' />
        <Metric label='Latência P95 no servidor' value={formatMs(metrics.p95_api_latency_ms)} icon={Gauge} color='#e0962f' background='#fff5e4' />
        <Metric label='Erros totais (app + APIs)' value={formatNumber(metrics.total_errors)} icon={AlertTriangle} color='#d84e5d' background='#fff0f2' />
        <Metric label='Taxa de erro no servidor' value={formatPercent(metrics.api_error_rate)} icon={Zap} color='#d84e5d' background='#fff0f2' />
      </div>

      <div className='ia-grid ia-grid--2'>
        <Panel title='Evolução de uso' subtitle='Pessoas são únicas em cada intervalo e não devem ser somadas entre dias ou horas' badge={data?.period?.granularity === 'hour' ? 'por hora' : 'por dia'}>
          <div className='ia-chart'>
            <ResponsiveContainer width='100%' height='100%'>
              <AreaChart data={data.evolution} margin={{top: 8, right: 8, left: -18, bottom: 0}}>
                <defs>
                  <linearGradient id='viewsGradient' x1='0' y1='0' x2='0' y2='1'><stop offset='0%' stopColor='#3157d5' stopOpacity='.28' /><stop offset='100%' stopColor='#3157d5' stopOpacity='.02' /></linearGradient>
                  <linearGradient id='usersGradient' x1='0' y1='0' x2='0' y2='1'><stop offset='0%' stopColor='#0e9f8a' stopOpacity='.22' /><stop offset='100%' stopColor='#0e9f8a' stopOpacity='.01' /></linearGradient>
                </defs>
                <CartesianGrid vertical={false} strokeDasharray='3 3' />
                <XAxis dataKey='bucket' tickFormatter={(value) => data?.period?.granularity === 'hour' ? new Date(value).toLocaleTimeString('pt-BR', {hour: '2-digit'}) : new Date(value).toLocaleDateString('pt-BR', {day: '2-digit', month: 'short'})} axisLine={false} tickLine={false} />
                <YAxis axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip content={<ChartTooltip />} />
                <Legend iconType='circle' iconSize={7} wrapperStyle={{fontSize: 10, color: '#667085'}} />
                <Area type='monotone' dataKey='page_views' name='Visualizações' stroke='#3157d5' strokeWidth={2} fill='url(#viewsGradient)' />
                <Area type='monotone' dataKey='users' name='Usuários com navegação' stroke='#0e9f8a' strokeWidth={2} fill='url(#usersGradient)' />
                <Area type='monotone' dataKey='feature_uses' name='Features' stroke='#e4a23b' strokeWidth={1.7} fill='transparent' />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Panel>

        <Panel title='Dispositivos' subtitle='Distribuição das visualizações por categoria' badge={`${formatNumber(data.devices?.reduce((total: number, item: any) => total + item.value, 0))} visualizações`}>
          <div className='ia-chart'>
            <ResponsiveContainer width='100%' height='100%'>
              <PieChart>
                <Pie data={data.devices} dataKey='value' nameKey='name' innerRadius='54%' outerRadius='76%' paddingAngle={3} stroke='none'>
                  {data.devices?.map((_: any, index: number) => <Cell key={index} fill={colors[index % colors.length]} />)}
                </Pie>
                <Tooltip formatter={(value: any) => formatNumber(value)} contentStyle={{border: '1px solid #e4e7ec', borderRadius: 9, fontSize: 10}} />
                <Legend iconType='circle' iconSize={7} wrapperStyle={{fontSize: 10}} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </Panel>
      </div>

      <div className='ia-grid ia-grid--equal'>
        <Panel title='Telas mais acessadas' subtitle='Ranking por volume de visualizações'>
          <div className='ia-list'>
            {data.top_pages?.map((item: any, index: number) => (
              <div className='ia-list-item' key={item.path}>
                <span className='ia-list-item__rank'>{String(index + 1).padStart(2, '0')}</span>
                <div className='ia-list-item__main'><div className='ia-list-item__title'>{item.name || item.path}</div><div className='ia-list-item__meta'>{item.path} · {formatNumber(item.users)} usuários únicos nesta tela</div></div>
                <span className='ia-list-item__value'>{formatNumber(item.views)}</span>
              </div>
            ))}
          </div>
        </Panel>
        <Panel title='Atividade recente' subtitle='Últimos eventos observados na plataforma'>
          <div className='ia-list'>
            {data.recent_activity?.map((item: any, index: number) => (
              <div className='ia-list-item' key={`${item.occurred_at}-${index}`}>
                <span className='ia-activity-icon'>{item.event_type === 'feature_use' ? <MousePointerClick size={13} /> : <Activity size={13} />}</span>
                <div className='ia-list-item__main'><div className='ia-list-item__title'>{item.user_name || 'Usuário'} · {item.event_name || item.path}</div><div className='ia-list-item__meta'>{item.company_name || 'Sem empresa'} · {formatDate(item.occurred_at)}</div></div>
              </div>
            ))}
          </div>
        </Panel>
      </div>

      <Panel title='Features mais utilizadas' subtitle='Usuários são distintos por funcionalidade; não some a coluna entre linhas' badge={`${formatNumber(metrics.feature_uses)} usos`}>
        <div className='ia-table-wrap'>
          <table className='ia-table'><thead><tr><th>Feature</th><th>Tela</th><th>Usos</th><th>Usuários únicos</th></tr></thead>
            <tbody>{data.top_features?.map((item: any) => <tr key={`${item.name}-${item.path}`}><td><strong>{item.name}</strong></td><td><span className='ia-table__path'>{item.path}</span></td><td>{formatNumber(item.uses)}</td><td>{formatNumber(item.users)}</td></tr>)}</tbody>
          </table>
        </div>
      </Panel>
    </>
  )
}

const Screens = ({data, search, setSearch}: {data: any; search: string; setSearch: (value: string) => void}) => {
  const rows = useMemo(() => (data?.pages || []).filter((item: any) => `${item.name} ${item.path}`.toLowerCase().includes(search.toLowerCase())), [data, search])
  if (!data?.pages?.length) return <Empty title='Nenhuma tela registrada no período' />
  return (
    <>
      <div className='ia-scope-note'><strong>Como interpretar:</strong> usuários e sessões são distintos dentro de cada tela. Não some essas colunas entre telas, pois a mesma pessoa ou sessão pode aparecer em várias linhas.</div>
      <Panel title='As 12 telas com maior alcance' subtitle='Comparativo direto de visualizações no período selecionado'>
        <div className='ia-chart ia-chart--compact'><ResponsiveContainer width='100%' height='100%'><BarChart data={data.pages.slice(0, 12)} margin={{top: 8, right: 8, left: -15, bottom: 30}}><CartesianGrid vertical={false} strokeDasharray='3 3' /><XAxis dataKey='name' tickFormatter={(value) => String(value || '').slice(0, 13)} angle={-25} textAnchor='end' axisLine={false} tickLine={false} /><YAxis axisLine={false} tickLine={false} /><Tooltip content={<ChartTooltip />} /><Bar dataKey='views' name='Visualizações' fill='#3157d5' radius={[6, 6, 0, 0]} maxBarSize={34} /></BarChart></ResponsiveContainer></div>
      </Panel>
      <div style={{height: 14}} />
      <Panel title='Desempenho por tela' subtitle={`${formatNumber(rows.length)} telas encontradas`}>
        <div style={{display: 'flex', justifyContent: 'flex-end', marginBottom: 12}}><SearchBox value={search} onChange={setSearch} placeholder='Buscar por nome ou rota' /></div>
        <div className='ia-table-wrap'><table className='ia-table'><thead><tr><th>Tela</th><th>Visualizações</th><th>Usuários únicos</th><th>Sessões</th><th>Tempo médio</th><th>Taxa de rejeição</th><th>Erros no navegador</th><th>Último acesso</th></tr></thead>
          <tbody>{rows.map((item: any) => <tr key={item.path}><td><strong>{item.name || item.path}</strong><span className='ia-table__path'>{item.path}</span></td><td>{formatNumber(item.views)}</td><td>{formatNumber(item.users)}</td><td>{formatNumber(item.sessions)}</td><td>{formatMs(item.avg_time_ms)}</td><td>{formatPercent(item.bounce_rate)}</td><td className={item.frontend_errors ? 'ia-error-count' : ''}>{formatNumber(item.frontend_errors)}</td><td>{formatDate(item.last_access)}</td></tr>)}</tbody>
        </table></div>
      </Panel>
    </>
  )
}

const UsersReport = ({data, search, setSearch}: {data: any; search: string; setSearch: (value: string) => void}) => {
  const rows = useMemo(() => (data?.users || []).filter((item: any) => `${item.name} ${item.email} ${item.company}`.toLowerCase().includes(search.toLowerCase())), [data, search])
  if (!data?.users?.length) return <Empty title='Nenhum usuário com navegação rastreada no período' />
  return (
    <>
      <div className='ia-scope-note'><strong>Escopo desta seção:</strong> considera somente eventos de navegação capturados pelo coletor Analytics. Dashboards/Relatórios usa logs históricos de conteúdo e pode apresentar mais usuários.</div>
      <div className='ia-grid ia-grid--2'>
        <div className='ia-metrics' style={{alignContent: 'start'}}>
          <Metric label='Usuários com navegação' value={formatNumber(data.total_users)} icon={Users} hint='no período' />
          <Metric label='Sessões com navegação' value={formatNumber(data.total_sessions)} icon={Activity} hint='no período' color='#0e9f8a' background='#e9faf6' />
        </div>
        <Panel title='Usuários mais ativos' subtitle='Ranking por visualizações registradas pelo coletor Analytics'>
          <div className='ia-chart ia-chart--compact'><ResponsiveContainer width='100%' height='100%'><BarChart data={data.users.slice(0, 8)} layout='vertical' margin={{left: 15, right: 12}}><CartesianGrid horizontal={false} strokeDasharray='3 3' /><XAxis type='number' axisLine={false} tickLine={false} /><YAxis type='category' dataKey='name' width={95} tickFormatter={(value) => String(value).split(' ')[0]} axisLine={false} tickLine={false} /><Tooltip content={<ChartTooltip />} /><Bar dataKey='views' name='Visualizações' fill='#3157d5' radius={[0, 6, 6, 0]} maxBarSize={18} /></BarChart></ResponsiveContainer></div>
        </Panel>
      </div>
      <Panel title='Engajamento por usuário' subtitle={data.list_truncated ? `${formatNumber(rows.length)} exibidos de ${formatNumber(data.total_users)}` : `${formatNumber(rows.length)} pessoas no recorte`}>
        <div style={{display: 'flex', justifyContent: 'flex-end', marginBottom: 12}}><SearchBox value={search} onChange={setSearch} placeholder='Buscar nome, e-mail ou empresa' /></div>
        <div className='ia-table-wrap'><table className='ia-table'><thead><tr><th>Usuário</th><th>Empresa(s) no período</th><th>Visualizações</th><th>Sessões</th><th>Dias com navegação</th><th>Usos de funcionalidades</th><th>Tela mais acessada no recorte</th><th>Última atividade</th></tr></thead>
          <tbody>{rows.map((item: any) => <tr key={item.id_user}><td><div className='ia-user'><span className='ia-avatar'>{initials(item.name)}</span><div className='ia-user__meta'><strong>{item.name}</strong><span>{item.email}</span></div></div></td><td>{item.company || '—'}</td><td>{formatNumber(item.views)}</td><td>{formatNumber(item.sessions)}</td><td>{formatNumber(item.active_days)}</td><td>{formatNumber(item.feature_uses)}</td><td><strong>{item.favorite_screen_name || '—'}</strong><span className='ia-table__path'>{item.favorite_screen || '—'}</span></td><td>{formatDate(item.last_activity)}</td></tr>)}</tbody>
        </table></div>
      </Panel>
    </>
  )
}

const FeaturesReport = ({data, search, setSearch}: {data: any; search: string; setSearch: (value: string) => void}) => {
  const groups = data?.groups || []
  const rows = useMemo(() => (data?.features || []).filter((item: any) => `${item.group} ${item.name} ${item.path}`.toLowerCase().includes(search.toLowerCase())), [data, search])
  if (!groups.length) return <Empty title='Nenhuma interação registrada no período' description='As interações aparecerão agrupadas por área de negócio conforme as pessoas utilizarem a plataforma.' />
  return (
    <>
      <div className='ia-scope-note'><strong>Como interpretar:</strong> as ações foram consolidadas por área de negócio. Usuários e sessões são distintos dentro de cada grupo e não devem ser somados entre grupos.</div>
      <Panel title='Interações por área de negócio' subtitle='Visão consolidada do uso de Dashboards, Relatórios, Alertas, Nível de Serviço, Gira+ e Copiloto'>
        <div className='ia-chart'><ResponsiveContainer width='100%' height='100%'><BarChart data={groups} layout='vertical' margin={{left: 45, right: 15}}><CartesianGrid horizontal={false} strokeDasharray='3 3' /><XAxis type='number' allowDecimals={false} axisLine={false} tickLine={false} /><YAxis type='category' dataKey='name' width={165} tickFormatter={(value) => String(value).slice(0, 28)} axisLine={false} tickLine={false} /><Tooltip content={<ChartTooltip />} /><Bar dataKey='uses' name='Interações' fill='#0e9f8a' radius={[0, 6, 6, 0]} maxBarSize={22} /></BarChart></ResponsiveContainer></div>
      </Panel>
      <div style={{height: 14}} />
      <Panel title='Detalhamento das interações' subtitle={`${formatNumber(rows.length)} combinações de ação e tela`}>
        <div style={{display: 'flex', justifyContent: 'flex-end', marginBottom: 12}}><SearchBox value={search} onChange={setSearch} placeholder='Buscar área, interação ou rota' /></div>
        <div className='ia-table-wrap'><table className='ia-table'><thead><tr><th>Área de negócio</th><th>Interação</th><th>Tela</th><th>Total de usos</th><th>Usuários únicos</th><th>Sessões</th><th>Último uso</th></tr></thead>
          <tbody>{rows.map((item: any) => <tr key={`${item.name}-${item.path}`}><td><span className='ia-method'>{item.group}</span></td><td><strong>{item.name}</strong></td><td><span className='ia-table__path'>{item.path}</span></td><td>{formatNumber(item.uses)}</td><td>{formatNumber(item.users)}</td><td>{formatNumber(item.sessions)}</td><td>{formatDate(item.last_use)}</td></tr>)}</tbody>
        </table></div>
      </Panel>
    </>
  )
}

const EndpointTable = ({rows}: {rows: any[]}) => (
  <div className='ia-table-wrap'><table className='ia-table'><thead><tr><th>Método</th><th>Endpoint</th><th>Requisições</th><th>Média</th><th>P95</th><th>Máximo</th><th>Taxa de erro</th></tr></thead>
    <tbody>{rows.map((item: any) => <tr key={`${item.method}-${item.endpoint}`}><td><span className='ia-method'>{item.method}</span></td><td><strong>{item.endpoint}</strong></td><td>{formatNumber(item.requests)}</td><td>{formatMs(item.average)}</td><td>{formatMs(item.p95)}</td><td>{formatMs(item.maximum)}</td><td className={item.error_rate ? 'ia-error-count' : ''}>{formatPercent(item.error_rate)}</td></tr>)}</tbody>
  </table></div>
)

const PerformanceReport = ({data}: {data: any}) => {
  const metrics = data?.metrics || {}
  if (!metrics.requests && !metrics.browser_requests && !data?.web_vitals?.length) return <Empty title='Sem amostras de performance no período' />
  return (
    <>
      <div className='ia-metrics'>
        <Metric label='Requisições no servidor' value={formatNumber(metrics.requests)} icon={Server} />
        <Metric label='Latência média no servidor' value={formatMs(metrics.average)} icon={Timer} color='#1684c7' background='#eaf6fd' />
        <Metric label='P95 no servidor' value={formatMs(metrics.p95)} icon={Gauge} color='#e0962f' background='#fff5e4' />
        <Metric label='Falhas HTTP no servidor' value={formatNumber(metrics.errors)} icon={AlertTriangle} color='#d84e5d' background='#fff0f2' />
        <Metric label='Taxa de erro no servidor' value={formatPercent(metrics.error_rate)} icon={Zap} color='#d84e5d' background='#fff0f2' />
        <Metric label='Média percebida no browser' value={formatMs(metrics.browser_average)} icon={Laptop} hint={`${formatNumber(metrics.browser_requests)} req.`} color='#7a58c7' background='#f3effd' />
        <Metric label='P95 percebido no browser' value={formatMs(metrics.browser_p95)} icon={Smartphone} color='#7a58c7' background='#f3effd' />
      </div>
      <div className='ia-grid ia-grid--2'>
        <Panel title='Endpoints mais lentos' subtitle='Ordenação pela latência P95 real do servidor'>
          <div className='ia-chart ia-chart--compact'><ResponsiveContainer width='100%' height='100%'><BarChart data={data.endpoints?.slice(0, 8)} layout='vertical' margin={{left: 25, right: 12}}><CartesianGrid horizontal={false} strokeDasharray='3 3' /><XAxis type='number' tickFormatter={(value) => `${Math.round(value)}ms`} axisLine={false} tickLine={false} /><YAxis type='category' dataKey='endpoint' width={120} tickFormatter={(value) => String(value).slice(0, 19)} axisLine={false} tickLine={false} /><Tooltip formatter={(value: any) => formatMs(value)} /><Bar dataKey='p95' name='P95' fill='#e49b36' radius={[0, 6, 6, 0]} maxBarSize={18} /></BarChart></ResponsiveContainer></div>
        </Panel>
        <Panel title='Web Vitals' subtitle='Experiência real de carregamento e estabilidade visual'>
          <div className='ia-vitals'>{data.web_vitals?.length ? data.web_vitals.map((item: any) => <div className='ia-vital' key={item.name}><div className='ia-vital__name'>{item.name}</div><div className='ia-vital__value'>{item.name === 'CLS' ? Number(item.average).toFixed(3) : formatMs(item.average)}</div><div className='ia-vital__meta'>Máx. {item.name === 'CLS' ? Number(item.maximum).toFixed(3) : formatMs(item.maximum)} · {formatNumber(item.samples)} amostras</div></div>) : <Empty title='Sem Web Vitals' description='As amostras surgirão após novos carregamentos da aplicação.' />}</div>
        </Panel>
      </div>
      <Panel title='Performance por endpoint' subtitle='Medição executada no middleware do servidor' badge={`${formatNumber(data.endpoints?.length)} rotas`}><EndpointTable rows={data.endpoints || []} /></Panel>
      <div style={{height: 14}} />
      <Panel title='Latência percebida no navegador' subtitle='Inclui rede, proxy e tempo de resposta até o browser' badge={`${formatNumber(data.browser_endpoints?.length)} rotas`}><EndpointTable rows={data.browser_endpoints || []} /></Panel>
      <div style={{height: 14}} />
      <Panel title='Erros recentes' subtitle='Até 100 ocorrências mais recentes: falhas HTTP, erros JavaScript e promises rejeitadas'>
        {data.recent_errors?.length ? <div className='ia-table-wrap'><table className='ia-table'><thead><tr><th>Tipo</th><th>Erro</th><th>Usuário</th><th>Tela</th><th>Status</th><th>Data e hora</th></tr></thead><tbody>{data.recent_errors.map((item: any, index: number) => <tr key={`${item.occurred_at}-${index}`}><td><span className='ia-method'>{item.event_type === 'api_request' ? 'API' : item.event_type === 'client_api_request' ? 'HTTP/browser' : 'JS'}</span></td><td><strong>{item.message || item.event_name || 'Falha sem mensagem'}</strong></td><td>{item.user_name || '—'}</td><td><span className='ia-table__path'>{item.path}</span></td><td className='ia-error-count'>{item.status || '—'}</td><td>{formatDate(item.occurred_at)}</td></tr>)}</tbody></table></div> : <Empty title='Nenhum erro encontrado' description='Tudo certo no período e nos filtros selecionados.' />}
      </Panel>
    </>
  )
}

const Realtime = ({data}: {data: any}) => {
  const metrics = data?.metrics || {}
  if (!data?.sessions?.length) return <Empty title='Nenhuma atividade recente' description='Uma sessão aparece aqui quando envia heartbeat nos últimos 30 minutos.' />
  return (
    <>
      <div className='ia-scope-note'><strong>Como interpretar:</strong> usuários são pessoas distintas; os demais cartões contam sessões/abas. Sessões visíveis + sessões em segundo plano = sessões recentes.</div>
      <div className='ia-metrics'>
        <Metric label='Usuários ativos' value={formatNumber(metrics.online_users)} icon={Wifi} hint='últimos 30 min' color='#0e9f8a' background='#e9faf6' />
        <Metric label='Sessões visíveis' value={formatNumber(metrics.visible_tabs)} icon={Eye} color='#1684c7' background='#eaf6fd' />
        <Metric label='Sessões em 2º plano' value={formatNumber(metrics.background_tabs)} icon={Layers3} color='#e0962f' background='#fff5e4' />
        <Metric label='Sessões recentes' value={formatNumber(metrics.active_sessions)} icon={Activity} hint='30 min' />
      </div>
      <div className='ia-grid ia-grid--2'>
        <Panel title='Telas com atividade recente' subtitle='Distribuição das sessões observadas nos últimos 30 minutos'>
          <div className='ia-chart ia-chart--compact'><ResponsiveContainer width='100%' height='100%'><BarChart data={data.screens?.slice(0, 10)} layout='vertical' margin={{left: 25, right: 12}}><CartesianGrid horizontal={false} strokeDasharray='3 3' /><XAxis type='number' allowDecimals={false} axisLine={false} tickLine={false} /><YAxis type='category' dataKey='name' width={120} tickFormatter={(value) => String(value).slice(0, 19)} axisLine={false} tickLine={false} /><Tooltip content={<ChartTooltip />} /><Bar dataKey='sessions' name='Sessões' fill='#0e9f8a' radius={[0, 6, 6, 0]} maxBarSize={18} /></BarChart></ResponsiveContainer></div>
        </Panel>
        <Panel title='Atualização automática' subtitle='A presença é sincronizada a cada 15 segundos'>
          <div style={{display: 'flex', height: 235, flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center'}}><span className='ia-empty__icon' style={{background: '#e9faf6', color: '#0e9f8a'}}><Radio size={22} /></span><strong style={{fontSize: 15, color: '#263044'}}>Monitoramento ativo</strong><span style={{marginTop: 6, color: '#8a92a1', fontSize: 10}}>Última sincronização em {formatDate(data.synced_at)}</span></div>
        </Panel>
      </div>
      <Panel title='Sessões dos últimos 30 minutos' subtitle='Cada aba representa uma sessão; o status indica o último estado informado' badge={`${formatNumber(data.sessions.length)} sessões`}>
        <div className='ia-table-wrap'><table className='ia-table'><thead><tr><th>Último estado</th><th>Usuário</th><th>Empresa</th><th>Última tela</th><th>Dispositivo</th><th>Navegador</th><th>Início</th><th>Último heartbeat</th></tr></thead><tbody>{data.sessions.map((item: any) => <tr key={item.session_id}><td><span className={`ia-status ${item.is_visible ? 'ia-status--online' : 'ia-status--away'}`}><i />{item.is_visible ? 'Visível' : '2º plano'}</span></td><td><div className='ia-user'><span className='ia-avatar'>{initials(item.user_name)}</span><div className='ia-user__meta'><strong>{item.user_name || 'Usuário'}</strong><span>{item.email || '—'}</span></div></div></td><td>{item.company_name || '—'}</td><td><strong>{item.screen_name || item.path}</strong><span className='ia-table__path'>{item.path}</span></td><td>{item.device || '—'}</td><td>{item.browser || '—'}</td><td>{formatDate(item.session_started_at)}</td><td>{formatDate(item.last_heartbeat_at)}</td></tr>)}</tbody></table></div>
      </Panel>
    </>
  )
}

type ExportColumn = {
  key: string
  label: string
  value?: (row: any) => unknown
}

const exportRows = (rows: any[], columns: ExportColumn[]) => rows.map((row) => Object.fromEntries(
  columns.map(({key, label, value}) => {
    const cell = value ? value(row) : row[key]
    return [label, cell ?? '']
  })
))

const exportMetrics = (metrics: any, labels: Array<[string, string]>) => labels.map(([key, label]) => ({
  Indicador: label,
  Valor: metrics?.[key] ?? 0,
}))

const formatExportDate = (value: unknown) => {
  if (!value) return ''
  const date = new Date(String(value))
  if (Number.isNaN(date.getTime())) return String(value)
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  }).format(date)
}

const exportEventLabels: Record<string, string> = {
  page_view: 'Visualiza\u00e7\u00e3o de tela',
  page_duration: 'Perman\u00eancia na tela',
  feature_use: 'Uso de funcionalidade',
  javascript_error: 'Erro JavaScript',
  unhandled_rejection: 'Promise rejeitada',
  api_request: 'Requisi\u00e7\u00e3o da API',
  client_api_request: 'Requisi\u00e7\u00e3o do navegador',
  web_vital: 'M\u00e9trica de experi\u00eancia',
}

const exportEventType = (value: unknown) => exportEventLabels[String(value || '')] || String(value || 'N\u00e3o informado')

const exportDeviceLabels: Record<string, string> = {
  desktop: 'Computador',
  mobile: 'Celular',
  tablet: 'Tablet',
  unknown: 'Desconhecido',
}

const exportDevice = (value: unknown) => exportDeviceLabels[String(value || '').toLowerCase()] || String(value || 'Desconhecido')

const exportSheets = (section: AnalyticsSection, data: any) => {
  const sheets: Record<string, any[]> = {}

  if (section === 'overview') {
    sheets.Resumo = exportMetrics(data.metrics, [
      ['online_users', 'Usu\u00e1rios ativos (\u00daltimos 30 minutos)'],
      ['active_sessions', 'Sess\u00f5es recentes (\u00daltimos 30 minutos)'],
      ['page_views', 'Visualiza\u00e7\u00f5es de tela'],
      ['sessions', 'Sess\u00f5es no per\u00edodo'],
      ['unique_users', 'Usu\u00e1rios com navega\u00e7\u00e3o rastreada'],
      ['avg_screen_time_ms', 'Tempo m\u00e9dio por tela (ms)'],
      ['avg_api_latency_ms', 'Lat\u00eancia m\u00e9dia no servidor (ms)'],
      ['p95_api_latency_ms', 'Lat\u00eancia P95 no servidor (ms)'],
      ['total_errors', 'Erros totais (app + APIs)'],
      ['api_error_rate', 'Taxa de erro no servidor (%)'],
      ['feature_uses', 'Usos de funcionalidades'],
    ])
    sheets['Evolu\u00e7\u00e3o'] = exportRows(data.evolution || [], [
      {key: 'bucket', label: 'Data e hora', value: (row) => formatExportDate(row.bucket)},
      {key: 'page_views', label: 'Visualiza\u00e7\u00f5es de tela'},
      {key: 'users', label: 'Usu\u00e1rios com navega\u00e7\u00e3o no intervalo'},
      {key: 'feature_uses', label: 'Usos de funcionalidades'},
    ])
    sheets.Telas = exportRows(data.top_pages || [], [
      {key: 'name', label: 'Tela'},
      {key: 'path', label: 'Rota'},
      {key: 'views', label: 'Visualiza\u00e7\u00f5es'},
      {key: 'users', label: 'Usu\u00e1rios \u00fanicos'},
    ])
    sheets.Funcionalidades = exportRows(data.top_features || [], [
      {key: 'name', label: 'Funcionalidade'},
      {key: 'path', label: 'Tela/rota'},
      {key: 'uses', label: 'Total de usos'},
      {key: 'users', label: 'Usu\u00e1rios \u00fanicos'},
    ])
    sheets.Dispositivos = exportRows(data.devices || [], [
      {key: 'name', label: 'Dispositivo', value: (row) => exportDevice(row.name)},
      {key: 'value', label: 'Visualiza\u00e7\u00f5es'},
    ])
    sheets.Atividade = exportRows(data.recent_activity || [], [
      {key: 'occurred_at', label: 'Data e hora', value: (row) => formatExportDate(row.occurred_at)},
      {key: 'user_name', label: 'Nome do Usu\u00e1rio'},
      {key: 'company_name', label: 'Empresa'},
      {key: 'event_type', label: 'Tipo de Evento', value: (row) => exportEventType(row.event_type)},
      {key: 'event_name', label: 'Evento'},
      {key: 'path', label: 'Tela/rota'},
    ])
  } else if (section === 'screens') {
    sheets.Telas = exportRows(data.pages || [], [
      {key: 'name', label: 'Tela'},
      {key: 'path', label: 'Rota'},
      {key: 'views', label: 'Visualiza\u00e7\u00f5es'},
      {key: 'users', label: 'Usu\u00e1rios \u00fanicos'},
      {key: 'sessions', label: 'Sess\u00f5es'},
      {key: 'avg_time_ms', label: 'Tempo m\u00e9dio na tela (ms)'},
      {key: 'bounce_rate', label: 'Taxa de rejei\u00e7\u00e3o (%)'},
      {key: 'frontend_errors', label: 'Erros no navegador'},
      {key: 'last_access', label: '\u00daltimo acesso', value: (row) => formatExportDate(row.last_access)},
    ])
  } else if (section === 'users') {
    sheets['Usu\u00e1rios'] = exportRows(data.users || [], [
      {key: 'name', label: 'Nome do Usu\u00e1rio'},
      {key: 'email', label: 'E-mail'},
      {key: 'company', label: 'Empresa(s) no per\u00edodo'},
      {key: 'views', label: 'Visualiza\u00e7\u00f5es'},
      {key: 'sessions', label: 'Sess\u00f5es'},
      {key: 'active_days', label: 'Dias com navega\u00e7\u00e3o'},
      {key: 'feature_uses', label: 'Usos de funcionalidades'},
      {key: 'favorite_screen_name', label: 'Tela mais acessada no recorte'},
      {key: 'favorite_screen', label: 'Rota da tela mais acessada no recorte'},
      {key: 'last_activity', label: '\u00daltima atividade', value: (row) => formatExportDate(row.last_activity)},
    ])
  } else if (section === 'features') {
    sheets['\u00c1reas de neg\u00f3cio'] = exportRows(data.groups || [], [
      {key: 'name', label: '\u00c1rea de neg\u00f3cio'},
      {key: 'uses', label: 'Total de intera\u00e7\u00f5es'},
      {key: 'users', label: 'Usu\u00e1rios \u00fanicos'},
      {key: 'sessions', label: 'Sess\u00f5es'},
      {key: 'last_use', label: '\u00daltima intera\u00e7\u00e3o', value: (row) => formatExportDate(row.last_use)},
    ])
    sheets['Intera\u00e7\u00f5es'] = exportRows(data.features || [], [
      {key: 'group', label: '\u00c1rea de neg\u00f3cio'},
      {key: 'name', label: 'Intera\u00e7\u00e3o'},
      {key: 'path', label: 'Tela/rota'},
      {key: 'uses', label: 'Total de usos'},
      {key: 'users', label: 'Usu\u00e1rios \u00fanicos'},
      {key: 'sessions', label: 'Sess\u00f5es'},
      {key: 'last_use', label: '\u00daltimo uso', value: (row) => formatExportDate(row.last_use)},
    ])
  } else if (section === 'content') {
    const isReports = data.kind === 'reports'
    const itemLabel = isReports ? 'Relatório' : 'Dashboard'
    const metricLabels: Array<[string, string]> = [
      ['accesses', isReports ? 'Interações registradas' : 'Acessos únicos'],
      ...(isReports ? [['views', 'Visualizações de relatórios'], ['exports', 'Exportações de relatórios']] as Array<[string, string]> : []),
      ['unique_users', isReports ? 'Usuários com acesso a relatórios' : 'Usuários com acesso a dashboards'],
      ['unique_companies', 'Empresas atuais de referência'],
      ['unique_items', isReports ? 'Relatórios acessados' : 'Dashboards acessados'],
    ]
    sheets.Resumo = exportMetrics(data.metrics, metricLabels)
    sheets['Evolução'] = exportRows(data.evolution || [], [
      {key: 'bucket', label: 'Data', value: (row) => formatExportDate(row.bucket)},
      ...(isReports ? [{key: 'views', label: 'Visualizações'}, {key: 'exports', label: 'Exportações'}] : [{key: 'accesses', label: 'Acessos únicos'}]),
      {key: 'users', label: 'Usuários com acesso'},
    ])
    sheets.Conteúdos = exportRows(data.top_items || [], [
      {key: 'name', label: itemLabel},
      ...(isReports ? [{key: 'views', label: 'Visualizações'}, {key: 'exports', label: 'Exportações'}] : [{key: 'accesses', label: 'Acessos únicos'}]),
      {key: 'users', label: 'Usuários com acesso'},
    ])
    sheets.Empresas = exportRows(data.companies || [], [
      {key: 'name', label: 'Empresa atual de referência'},
      {key: 'accesses', label: isReports ? 'Interações' : 'Acessos únicos'},
      {key: 'users', label: 'Usuários com acesso'},
    ])
    sheets[isReports ? 'Interações' : 'Acessos'] = exportRows(data.rows || [], [
      {key: 'content', label: itemLabel, value: (row) => isReports
        ? (row.content_name || row.log_name || 'Relatório não identificado')
        : (row.dashboard_name || row.log_name || 'Dashboard não identificado')},
      ...(isReports ? [{key: 'action_type', label: 'Ação'}] : []),
      {key: 'user_name', label: 'Nome do Usuário'},
      {key: 'email', label: 'E-mail'},
      {key: 'company_name', label: 'Empresa atual de referência'},
      {key: 'company_type', label: 'Tipo da empresa de referência'},
      {key: 'log_created_at', label: 'Data e hora', value: (row) => formatExportDate(row.log_created_at)},
    ])
  } else if (section === 'performance') {
    sheets.Resumo = exportMetrics(data.metrics, [
      ['requests', 'Requisi\u00e7\u00f5es no servidor'],
      ['average', 'Lat\u00eancia m\u00e9dia no servidor (ms)'],
      ['p95', 'Lat\u00eancia P95 no servidor (ms)'],
      ['errors', 'Falhas HTTP no servidor'],
      ['error_rate', 'Taxa de erro no servidor (%)'],
      ['browser_requests', 'Requisi\u00e7\u00f5es do navegador'],
      ['browser_average', 'Lat\u00eancia m\u00e9dia do navegador (ms)'],
      ['browser_p95', 'Lat\u00eancia P95 do navegador (ms)'],
    ])
    const endpointColumns: ExportColumn[] = [
      {key: 'method', label: 'M\u00e9todo HTTP'},
      {key: 'endpoint', label: 'Endpoint'},
      {key: 'requests', label: 'Requisi\u00e7\u00f5es'},
      {key: 'average', label: 'Lat\u00eancia m\u00e9dia (ms)'},
      {key: 'p95', label: 'Lat\u00eancia P95 (ms)'},
      {key: 'maximum', label: 'Lat\u00eancia m\u00e1xima (ms)'},
      {key: 'error_rate', label: 'Taxa de erro (%)'},
    ]
    sheets.Endpoints = exportRows(data.endpoints || [], endpointColumns)
    sheets['Requisi\u00e7\u00f5es do navegador'] = exportRows(data.browser_endpoints || [], endpointColumns)
    sheets['M\u00e9tricas da experi\u00eancia'] = exportRows(data.web_vitals || [], [
      {key: 'name', label: 'M\u00e9trica'},
      {key: 'average', label: 'Valor m\u00e9dio (ms)'},
      {key: 'maximum', label: 'Valor m\u00e1ximo (ms)'},
      {key: 'samples', label: 'Amostras'},
    ])
    sheets.Erros = exportRows(data.recent_errors || [], [
      {key: 'event_type', label: 'Tipo de Erro', value: (row) => exportEventType(row.event_type)},
      {key: 'message', label: 'Mensagem do Erro'},
      {key: 'event_name', label: 'Evento/endpoint'},
      {key: 'user_name', label: 'Nome do Usu\u00e1rio'},
      {key: 'company_name', label: 'Empresa'},
      {key: 'path', label: 'Tela/rota'},
      {key: 'method', label: 'M\u00e9todo HTTP'},
      {key: 'status', label: 'Status HTTP'},
      {key: 'duration_ms', label: 'Dura\u00e7\u00e3o (ms)'},
      {key: 'occurred_at', label: 'Data e hora', value: (row) => formatExportDate(row.occurred_at)},
    ])
  } else {
    sheets['Sess\u00f5es'] = exportRows(data.sessions || [], [
      {key: 'is_visible', label: '\u00daltimo estado', value: (row) => row.is_visible ? 'Vis\u00edvel' : 'Segundo plano'},
      {key: 'user_name', label: 'Nome do Usu\u00e1rio'},
      {key: 'email', label: 'E-mail'},
      {key: 'company_name', label: 'Empresa'},
      {key: 'screen_name', label: '\u00daltima tela'},
      {key: 'path', label: 'Rota'},
      {key: 'device', label: 'Dispositivo', value: (row) => exportDevice(row.device)},
      {key: 'browser', label: 'Navegador'},
      {key: 'session_started_at', label: 'In\u00edcio da sess\u00e3o', value: (row) => formatExportDate(row.session_started_at)},
      {key: 'last_heartbeat_at', label: '\u00daltima atividade', value: (row) => formatExportDate(row.last_heartbeat_at)},
    ])
    sheets.Telas = exportRows(data.screens || [], [
      {key: 'name', label: 'Tela'},
      {key: 'path', label: 'Rota'},
      {key: 'sessions', label: 'Sess\u00f5es recentes'},
      {key: 'users', label: 'Usu\u00e1rios \u00fanicos'},
    ])
  }

  return sheets
}

const exportFileNames: Record<AnalyticsSection, string> = {
  overview: 'visao-geral',
  screens: 'telas',
  users: 'usuarios',
  features: 'interacoes',
  content: 'conteudos',
  performance: 'performance',
  realtime: 'tempo-real',
}

const csvSheetBySection: Record<AnalyticsSection, string> = {
  overview: 'Telas',
  screens: 'Telas',
  users: 'Usu\u00e1rios',
  features: 'Intera\u00e7\u00f5es',
  content: 'Acessos',
  performance: 'Endpoints',
  realtime: 'Sess\u00f5es',
}

const downloadWorkbook = (section: AnalyticsSection, data: any) => {
  const workbook = XLSX.utils.book_new()
  Object.entries(exportSheets(section, data)).forEach(([name, rows]) => {
    const sheet = XLSX.utils.json_to_sheet(rows.length ? rows : [{'Informa\u00e7\u00e3o': 'Sem dados'}])
    XLSX.utils.book_append_sheet(workbook, sheet, name.slice(0, 31))
  })
  XLSX.writeFile(workbook, 'analytics-' + exportFileNames[section] + '-' + today() + '.xlsx')
}

const downloadCsv = (section: AnalyticsSection, data: any) => {
  const sheets = exportSheets(section, data)
  const sheetName = section === 'content' && data.kind === 'reports' ? 'Intera\u00e7\u00f5es' : csvSheetBySection[section]
  const rows = sheets[sheetName] || []
  const sheet = XLSX.utils.json_to_sheet(rows.length ? rows : [{'Informa\u00e7\u00e3o': 'Sem dados'}])
  const blob = new Blob(['\ufeff' + XLSX.utils.sheet_to_csv(sheet)], {type: 'text/csv;charset=utf-8'})
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = 'analytics-' + exportFileNames[section] + '-' + today() + '.csv'
  anchor.click()
  URL.revokeObjectURL(url)
}

const AnalyticsPage = () => {
  const navigate = useNavigate()
  const {section} = useParams<{section?: string}>()
  const activeSection = sections.some((item) => item.id === section) ? section as AnalyticsSection : 'overview'
  const [filters, setFilters] = useState<AnalyticsFilters>({from: daysAgo(30), to: today()})
  const [quickRange, setQuickRange] = useState(30)
  const [options, setOptions] = useState<any>({companies: [], users: [], paths: []})
  const [data, setData] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null)
  const [contentKind, setContentKind] = useState<ContentKind>('dashboards')
  const [contentResetKey, setContentResetKey] = useState(0)

  useEffect(() => {
    if (!section || !sections.some((item) => item.id === section)) navigate('/analytics/overview', {replace: true})
  }, [section, navigate])

  const loadOptions = useCallback(async () => {
    try { setOptions(await getAnalyticsFilters(filters)) } catch (_) {}
  }, [filters])

  const loadData = useCallback(async (silent = false) => {
    if (silent) setRefreshing(true)
    else setLoading(true)
    setError(null)
    try {
      setData(await getAnalyticsReport(activeSection, activeSection === 'content'
        ? {...filters, kind: contentKind}
        : filters
      ))
      setLastRefresh(new Date())
    } catch (requestError: any) {
      const code = requestError?.code || requestError?.response?.data?.error
      setError(code === 'AUTHENTICATION_FAIL' ? 'permission' : 'unavailable')
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }, [activeSection, filters, contentKind])

  useEffect(() => { loadData(); setSearch('') }, [loadData])
  useEffect(() => { loadOptions() }, [loadOptions])
  useEffect(() => {
    if (activeSection !== 'realtime') return
    const timer = window.setInterval(() => loadData(true), 15000)
    return () => window.clearInterval(timer)
  }, [activeSection, loadData])

  const setRange = (days: number) => {
    setQuickRange(days)
    setFilters((current) => ({...current, from: daysAgo(days), to: today()}))
  }

  const updateFilter = (key: keyof AnalyticsFilters, value: string) => {
    setQuickRange(0)
    setFilters((current) => ({...current, [key]: value || undefined, ...(key === 'company_guid' ? {user_guid: undefined} : {})}))
  }
  const clearFilters = () => {
    setQuickRange(30)
    setFilters({from: daysAgo(30), to: today()})
    setSearch('')
    setContentResetKey((current) => current + 1)
  }

  const content = () => {
    if (loading) return activeSection === 'content' ? <ContentAnalyticsLoader /> : <div className='ia-skeleton' />
    if (error) return (
      <div className='ia-error'><span className='ia-error__icon'><AlertTriangle size={22} /></span><h3>{error === 'permission' ? 'Acesso exclusivo para super admins' : 'Analytics temporariamente indisponível'}</h3><p>{error === 'permission' ? 'Seu perfil não possui permissão para consultar dados globais da plataforma.' : 'Não foi possível carregar os indicadores agora. A coleta continua operando em segundo plano.'}</p><button className='ia-action ia-action--primary' style={{marginTop: 14}} onClick={() => loadData()}><RefreshCw size={13} /> Tentar novamente</button></div>
    )
    if (activeSection === 'overview') return <Overview data={data} />
    if (activeSection === 'screens') return <Screens data={data} search={search} setSearch={setSearch} />
    if (activeSection === 'users') return <UsersReport data={data} search={search} setSearch={setSearch} />
    if (activeSection === 'features') return <FeaturesReport data={data} search={search} setSearch={setSearch} />
    if (activeSection === 'content') return <ContentAnalytics key={contentResetKey} data={data} kind={contentKind} onKindChange={setContentKind} />
    if (activeSection === 'performance') return <PerformanceReport data={data} />
    return <Realtime data={data} />
  }

  return (
    <div className='ia-page'>
      <PageTitle>Analytics</PageTitle>
      <header className='ia-hero'>
        <div className='ia-hero__content'><div className='ia-hero__eyebrow'><span className='ia-hero__eyebrow-dot' /> Inteligência operacional</div><h1>Analytics do Insight Experience</h1><p>Uma visão unificada de adoção, comportamento, performance e saúde técnica — da interação de cada pessoa à resposta de cada endpoint.</p></div>
        <div className='ia-hero__meta'><span className='ia-secure-pill'><ShieldCheck size={13} /> Ambiente Super Admin</span><span className='ia-live-pill'><i /> Coleta ativa</span></div>
      </header>

      <section className='ia-toolbar'>
        <div className='ia-toolbar__top'>
          <div className='ia-quick-range'>{[7, 30, 90].map((days) => <button key={days} className={quickRange === days ? 'active' : ''} onClick={() => setRange(days)}>{days}d</button>)}</div>
          <div className='ia-field'><label>Data inicial</label><input type='date' value={filters.from} max={filters.to} onChange={(event) => updateFilter('from', event.target.value)} /></div>
          <div className='ia-field'><label>Data final</label><input type='date' value={filters.to} min={filters.from} max={today()} onChange={(event) => updateFilter('to', event.target.value)} /></div>
          <div className='ia-field ia-field--wide'><label>Empresa</label><select value={filters.company_guid || ''} onChange={(event) => updateFilter('company_guid', event.target.value)}><option value=''>Todas as empresas</option>{options.companies?.map((item: any) => <option key={item.guid} value={item.guid}>{item.name}</option>)}</select></div>
          <div className='ia-field ia-field--wide'><label>Usuário</label><select value={filters.user_guid || ''} onChange={(event) => updateFilter('user_guid', event.target.value)}><option value=''>Todos os usuários</option>{options.users?.map((item: any) => <option key={item.guid} value={item.guid}>{item.name} · {item.company_name || item.email}</option>)}</select></div>
          {activeSection !== 'content' ? <div className='ia-field ia-field--wide'><label>Tela</label><select value={filters.path || ''} onChange={(event) => updateFilter('path', event.target.value)}><option value=''>Todas as telas</option>{options.paths?.map((path: string) => <option key={path} value={path}>{path}</option>)}</select></div> : null}
          <div className='ia-toolbar__actions'>
            <button className='ia-action' onClick={clearFilters}><RotateCcw size={13} /> Limpar filtros</button>
            <button className='ia-action' onClick={() => loadData(true)} disabled={refreshing}><RefreshCw size={13} className={refreshing ? 'ia-rotate' : ''} /> Atualizar</button>
            <button className='ia-action' onClick={() => downloadCsv(activeSection, data)} disabled={!data}><Download size={13} /> CSV</button>
            <button className='ia-action ia-action--primary' onClick={() => downloadWorkbook(activeSection, data)} disabled={!data}><FileSpreadsheet size={13} /> Excel</button>
          </div>
        </div>
      </section>

      <nav className='ia-tabs' aria-label='Seções do Analytics'>
        {sections.map(({id, label, icon: Icon}) => <button key={id} className={`ia-tab ${id === activeSection ? 'active' : ''} ${id === 'realtime' ? 'ia-tab--live' : ''}`} onClick={() => navigate(`/analytics/${id}`)}><Icon size={14} /> {label}</button>)}
      </nav>

      {activeSection === 'realtime' && lastRefresh ? <div className='ia-last-sync' style={{justifyContent: 'flex-end', margin: '-8px 2px 10px'}}><Radio size={10} /> Última sincronização: {formatDate(lastRefresh.toISOString())}</div> : null}
      {content()}
    </div>
  )
}

export const SuperAdminAnalyticsRoute = () => isSuperAdmin() ? <AnalyticsPage /> : <Navigate to='/error/404' replace />
export default AnalyticsPage
